import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { Toggle } from '@/components/primitives/Toggle';
import { Icon } from '@/components/Icon';
import { PageTitle } from '@/components/PageTitle';
import { SidePanel } from '@/components/SidePanel';
import { ListRow, PanelForm, SaveBar, SettingsDanger, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, SettingsState, } from '@/components/settings/Settings';
import { ApiError } from '@/api/client';
import { apiErrorText } from '@/api/opengater/errors';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import { useCreateOgSquad, useDeleteOgSquad, useOgSquads, useUpdateOgSquad, } from '@/api/opengater/useLocations';
import { idOf } from '@/lib/idOf';
import { useUrlSelection } from '@/lib/useUrlSelection';
const CAP = 'squads.write';
/**
 * locations `admin: squads` — server groups: a list with a quick on/off; a squad
 * opens in the panel on the right (`?squad=<id>`, `new` to add one).
 */
export function OpengaterSquads() {
    const { allowed } = useOgAuth();
    const canEdit = allowed(CAP, 'admin');
    const q = useOgSquads();
    const { value: sel, open, close } = useUrlSelection('squad');
    const rows = q.data ?? [];
    const current = sel === 'new' ? 'new' : (rows.find((r) => String(r.id) === sel) ?? null);
    return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "\u0421\u043A\u0432\u0430\u0434\u044B", sub: "\u0413\u0440\u0443\u043F\u043F\u044B \u0441\u0435\u0440\u0432\u0435\u0440\u043E\u0432 (squads)", children: canEdit && (_jsxs(Button, { onClick: () => open('new'), children: [_jsx(Icon, { name: "plus", size: 15 }), " \u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C"] })) }), q.isLoading && _jsx(SettingsLoading, {}), !!q.error && !q.isLoading && (_jsx(SettingsNote, { tone: "error", children: apiErrorText(q.error, 'Не удалось загрузить сквады.') })), !q.isLoading && !q.error && rows.length === 0 && (_jsx(SettingsState, { children: "\u0421\u043A\u0432\u0430\u0434\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" })), rows.length > 0 && (_jsx(SettingsGroup, { children: rows.map((row) => (_jsx(SquadItem, { row: row, active: String(row.id) === sel, onOpen: () => open(String(row.id)) }, row.id))) })), _jsx(MotionConfig, { reducedMotion: "user", children: _jsx(AnimatePresence, { children: current && (_jsx(SidePanel, { size: "narrow", title: current === 'new' ? 'Новый сквад' : current.name, onClose: close, children: _jsx(SquadForm, { squad: current === 'new' ? null : current, onCreated: (id) => (id == null ? close() : open(String(id))), onDone: close }, current === 'new' ? 'new' : current.id) }, "squad")) }) })] }));
}
function SquadItem({ row, active, onOpen }) {
    const { allowed, markDenied } = useOgAuth();
    const canEdit = allowed(CAP, 'admin');
    const upd = useUpdateOgSquad();
    return (_jsx(ListRow, { title: row.name, sub: row.is_active
            ? row.description || undefined
            : ['Выключен', row.description].filter(Boolean).join(' · '), active: active, dim: !row.is_active, onOpen: onOpen, trailing: canEdit ? (_jsx(Toggle, { on: row.is_active, disabled: upd.isPending, onChange: (v) => upd.mutate({ id: row.id, patch: { is_active: v } }, {
                onError: (e) => {
                    if (e instanceof ApiError && e.status === 403)
                        markDenied(CAP);
                },
            }), "aria-label": row.is_active ? `Выключить: ${row.name}` : `Включить: ${row.name}` })) : undefined }));
}
function draftOf(sq) {
    return {
        name: sq?.name ?? '',
        description: sq?.description ?? '',
        is_active: sq?.is_active ?? true,
    };
}
function SquadForm({ squad, onCreated, onDone, }) {
    const isNew = squad === null;
    const { allowed, markDenied } = useOgAuth();
    const canEdit = allowed(CAP, 'admin');
    const create = useCreateOgSquad();
    const update = useUpdateOgSquad();
    const del = useDeleteOgSquad();
    const base = useMemo(() => draftOf(squad), [squad]);
    const [f, setF] = useState(base);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const valid = f.name.trim() !== '';
    const dirty = isNew || Object.keys(f).some((k) => f[k] !== base[k]);
    const saving = create.isPending || update.isPending;
    const busy = !canEdit || saving;
    const saveErr = create.error ?? update.error;
    const onErr = (e) => {
        if (e instanceof ApiError && e.status === 403)
            markDenied(CAP);
    };
    const submit = (e) => {
        e.preventDefault();
        if (!canEdit || !valid || !dirty || saving)
            return;
        const body = {
            name: f.name.trim(),
            description: f.description.trim() || null,
            is_active: f.is_active,
        };
        if (!squad) {
            create.mutate(body, { onSuccess: (created) => onCreated(idOf(created)), onError: onErr });
        }
        else {
            update.mutate({ id: squad.id, patch: body }, { onError: onErr });
        }
    };
    return (_jsxs(PanelForm, { meta: squad ? `#${squad.id}` : undefined, onSubmit: submit, children: [!canEdit && (_jsx(SettingsNote, { tone: "lock", children: "\u0422\u043E\u043B\u044C\u043A\u043E \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440: \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u044B \u0440\u043E\u043B\u0438 admin." })), _jsxs(SettingsGroup, { children: [_jsx(SettingsRow, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", wide: true, children: _jsx(Input, { value: f.name, mono: true, disabled: busy, placeholder: "de-fra-1", "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", autoFocus: isNew, onChange: (e) => set('name', e.target.value) }) }), _jsx(SettingsRow, { label: "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", wide: true, children: _jsx(Input, { value: f.description, disabled: busy, placeholder: "\u0413\u0435\u0440\u043C\u0430\u043D\u0438\u044F, \u0424\u0440\u0430\u043D\u043A\u0444\u0443\u0440\u0442", "aria-label": "\u041E\u043F\u0438\u0441\u0430\u043D\u0438\u0435", onChange: (e) => set('description', e.target.value) }) }), _jsx(SettingsRow, { label: "\u0410\u043A\u0442\u0438\u0432\u0435\u043D", children: _jsx(Toggle, { on: f.is_active, disabled: busy, onChange: (v) => set('is_active', v), "aria-label": "\u0410\u043A\u0442\u0438\u0432\u0435\u043D" }) })] }), canEdit && squad && (_jsx(SettingsDanger, { label: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0441\u043A\u0432\u0430\u0434", confirmText: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0441\u043A\u0432\u0430\u0434?", pending: del.isPending, error: del.error ? apiErrorText(del.error) : undefined, onConfirm: () => del.mutate(squad.id, { onSuccess: onDone, onError: onErr }) })), canEdit && (_jsx(SaveBar, { dirty: dirty, saving: saving, saved: update.isSuccess, canSave: valid && dirty && !saving, error: saveErr ? apiErrorText(saveErr) : undefined, onReset: isNew
                    ? onDone
                    : () => {
                        setF(base);
                        update.reset();
                    }, text: isNew ? 'Новый сквад' : undefined, submitLabel: isNew ? 'Создать' : undefined }))] }));
}
